import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Sparkles, Clock, Music, Compass, Target, ChevronRight, Waves, Hash } from "lucide-react";
import { toast } from "sonner";
import FrequencyPlayer from "@/components/tools/FrequencyPlayer";
import Numerology from "@/components/tools/Numerology";
import MeditationPlayer from "@/components/tools/MeditationPlayer";
import SanctuaryHeader from "@/components/gateway/SanctuaryHeader";

// One-sentence "what it is + how it helps" beneath each tool card.
const blurb = (t) => {
  if (t.type === "frequencies") return "Pure healing tones you simply listen to — they settle your nervous system and gently lift your mood.";
  if (t.type === "meditation") return "Layered binaural soundscapes for guided rest — they quiet racing thoughts and ease you into deep calm.";
  if (t.type === "numerology") return "A reflective life-path reading from your birth date — a fresh lens that helps you understand yourself.";
  return t.summary || "A guided practice that builds insight and steadies how you feel.";
};

const iconFor = (t) => (t.type === "frequencies" ? Music : t.type === "meditation" ? Waves : t.type === "numerology" ? Hash : Sparkles);

const Tools = () => {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/tools").then(({ data }) => setTools(data)).catch(() => {}); }, []);

  const open = (t) => { setActive(t); setInput(""); setFeedback(""); };

  const getFeedback = async () => {
    if (!input.trim()) { toast.info("Write your reflection first."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/tools/feedback", { tool_id: active.id, input });
      setFeedback(data.feedback);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const sound = tools.filter((t) => ["frequencies", "meditation"].includes(t.type));
  const reflection = tools.filter((t) => !["frequencies", "meditation"].includes(t.type));

  const ToolCard = ({ t }) => {
    const Icon = iconFor(t);
    return (
      <button onClick={() => open(t)} data-testid={`tool-${t.id}`}
        className="text-left bg-white rounded-3xl border border-linen-3 p-6 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300 flex flex-col">
        <span className="w-11 h-11 rounded-2xl bg-calm flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-brand" /></span>
        <h3 className="font-serif text-2xl text-ink">{t.title}</h3>
        <p className="text-sm text-ink-2 mt-1.5 leading-relaxed flex-1">{blurb(t)}</p>
        <p className="flex items-center gap-1.5 text-xs text-ink-mute mt-4"><Clock className="w-3.5 h-3.5" /> {t.duration}</p>
      </button>
    );
  };

  const GROUPS = [
    { v: "sound", icon: Music, label: "Sound Therapy",
      desc: "Healing frequencies and binaural meditation — audio medicine that calms your body and mind.", items: sound },
    { v: "reflection", icon: Compass, label: "Deep Reflection",
      desc: "Numerology and guided inner work — fresh lenses for understanding who you are.", items: reflection },
  ];

  return (
    <div className="space-y-8" data-testid="tools-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink">Depth Tools</h1>
          <p className="text-ink-2 mt-2">Everything, gently grouped. Open a section, then tap any practice.</p>
        </div>
        <SanctuaryHeader tone="light" className="shrink-0 pt-1" />
      </div>

      <Accordion type="single" collapsible defaultValue="sound" className="space-y-4">
        {GROUPS.map((g) => (
          <AccordionItem key={g.v} value={g.v} className="bg-linen-2 rounded-3xl border border-linen-3 px-5 sm:px-6" data-testid={`tools-group-${g.v}`}>
            <AccordionTrigger className="hover:no-underline py-5" data-testid={`tools-group-trigger-${g.v}`}>
              <span className="flex items-center gap-3 text-left">
                <span className="w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center shrink-0"><g.icon className="w-5 h-5 text-brand" /></span>
                <span>
                  <span className="block font-serif text-2xl text-ink">{g.label}</span>
                  <span className="block text-sm text-ink-2 font-normal mt-0.5">{g.desc}</span>
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6 pt-1">
              <div className="grid sm:grid-cols-2 gap-5">
                {g.items.map((t) => <ToolCard key={t.id} t={t} />)}
              </div>
              {g.items.length === 0 && <p className="text-sm text-ink-mute">Loading…</p>}
            </AccordionContent>
          </AccordionItem>
        ))}

        <AccordionItem value="progress" className="bg-linen-2 rounded-3xl border border-linen-3 px-5 sm:px-6" data-testid="tools-group-progress">
          <AccordionTrigger className="hover:no-underline py-5" data-testid="tools-group-trigger-progress">
            <span className="flex items-center gap-3 text-left">
              <span className="w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center shrink-0"><Target className="w-5 h-5 text-brand" /></span>
              <span>
                <span className="block font-serif text-2xl text-ink">Daily Progress</span>
                <span className="block text-sm text-ink-2 font-normal mt-0.5">Your Readiness micro-goals — tiny, low-pressure steps that help momentum feel light.</span>
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-6 pt-1">
            <button onClick={() => navigate("/gateway/readiness")} data-testid="tools-goto-readiness"
              className="w-full text-left bg-white rounded-3xl border border-linen-3 p-6 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300 flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-calm flex items-center justify-center shrink-0"><Target className="w-6 h-6 text-brand" /></span>
              <span className="flex-1">
                <span className="block font-serif text-2xl text-ink">Open your micro-goal checklist</span>
                <span className="block text-sm text-ink-2 mt-1 leading-relaxed">Aurea shapes gentle daily, weekly and yearly goals from your own reflections — it keeps growth doable, never overwhelming.</span>
              </span>
              <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
            </button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-linen rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto" data-testid="tool-dialog">
          {active && (
            <>
              <DialogHeader>
                <span className="text-xs text-accent uppercase tracking-wide">{active.category} · {active.duration}</span>
                <DialogTitle className="font-serif text-3xl text-ink">{active.title}</DialogTitle>
              </DialogHeader>
              <p className="text-ink-2">{active.summary}</p>
              {active.type === "frequencies" && <FrequencyPlayer frequencies={active.frequencies} />}
              {active.type === "meditation" && <MeditationPlayer states={active.states} />}
              {active.type === "numerology" && <Numerology meanings={active.meanings} />}
              {active.steps && (
                <ol className="space-y-3 mt-2">
                  {active.steps.map((s, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-7 h-7 rounded-full bg-brand text-linen text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-ink-2 leading-relaxed pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
              )}
              <div className="bg-white rounded-2xl border border-linen-3 p-4 mt-2">
                <p className="text-sm font-medium text-ink mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-brand" /> Your reflection</p>
                <Textarea value={input} onChange={(e) => setInput(e.target.value)} placeholder="Write what came up for you during this practice…" className="rounded-2xl bg-linen border-linen-3 min-h-[90px]" data-testid="tool-input" />
                <Button onClick={getFeedback} disabled={loading} className="rounded-full bg-brand hover:bg-brand-hover text-linen mt-3 w-full" data-testid="tool-feedback-submit">{loading ? "Reflecting…" : "Get feedback"}</Button>
                {feedback && <p className="text-sm text-ink-2 leading-relaxed mt-4 whitespace-pre-wrap bg-linen-2 rounded-2xl p-4" data-testid="tool-feedback">{feedback}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tools;
